import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useResultHandler } from "../../../hooks/useResultHandler";
import { useSurveyStatistics } from "./useSurveyStatistics";
import { useCreateSurvey } from "./useCreateSurvey";
import type { CreateSurveyRequest, SurveyFormValues } from "../types/survey.types";
import { createSurveySchema, getSurveyValidationMessage } from "../schemas/survey.schema";
import { toCreateSurveyRequest } from "../utils/surveyRequestMapper";

type RestoreState = {
  returnTo?: string;
  returnState?: unknown;
};

const getCreatedSurveyId = (data: { id?: string; _id?: string } | undefined) =>
  data?.id || data?._id || "";

export type CreateSurveyFormState = SurveyFormValues;

interface UseCreateSurveySubmissionParams {
  restoreState?: RestoreState;
}

export function useCreateSurveySubmission({ restoreState }: UseCreateSurveySubmissionParams = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleResult, handleError, handleMessage } = useResultHandler();
  const { refresh: refreshSurveyStats } = useSurveyStatistics();
  const { createSurvey: createSurveyRequest } = useCreateSurvey();
  const [loading, setLoading] = useState(false);
  const [loadingState, setLoadingState] = useState<"draft" | null>(null);

  const submitSurvey = useCallback(
    async (formState: CreateSurveyFormState) => {
      const request: CreateSurveyRequest = toCreateSurveyRequest(formState);
      const validationResult = createSurveySchema.safeParse(request);

      if (!validationResult.success) {
        handleMessage(getSurveyValidationMessage(validationResult.error));
        return;
      }

      try {
        setLoading(true);
        setLoadingState("draft");
        const response = await createSurveyRequest(request);
        const success = handleResult(response, {
          successMessage: t("surveys.messages.draft_saved"),
        });

        if (!success) return;
        await refreshSurveyStats();

        const surveyId = getCreatedSurveyId(response.data);
        if (surveyId) {
          navigate(`/survey/${surveyId}`, { replace: true });
        } else if (restoreState?.returnTo) {
          navigate(restoreState.returnTo, { state: restoreState.returnState });
        } else {
          navigate("/survey");
        }
      } catch (error: unknown) {
        console.error("Failed to create survey:", error);
        handleError(error, "An error occurred while creating the survey");
      } finally {
        setLoading(false);
        setLoadingState(null);
      }
    },
    [
      createSurveyRequest,
      handleError,
      handleMessage,
      handleResult,
      navigate,
      refreshSurveyStats,
      restoreState?.returnState,
      restoreState?.returnTo,
      t,
    ],
  );

  const handleSaveDraft = useCallback(
    async (formState: CreateSurveyFormState) => submitSurvey(formState),
    [submitSurvey],
  );

  return { handleSaveDraft, isLoading: loading, loadingState };
}

export type UseCreateSurveySubmissionReturn = ReturnType<typeof useCreateSurveySubmission>;
